import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Content, Header, Page } from '@backstage/core-components';
import type { SvgIconComponent } from '@material-ui/icons/index';
import StorageIcon from '@material-ui/icons/Storage';
import PieChartIcon from '@material-ui/icons/PieChart';
import CodeIcon from '@material-ui/icons/Code';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import ChatIcon from '@material-ui/icons/Chat';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import { useApi, featureFlagsApiRef } from '@backstage/core-plugin-api';
import { usePreviewAnalytics } from './usePreviewAnalytics';
import codeMigrationsLight from './images/light/code-migrations.png';
import codeMigrationsDark from './images/dark/code-migrations.png';
import fleetCensusLight from './images/light/fleet-census.png';
import techSummaryLight from './images/light/tech-summary.png';
import fleetCensusDark from './images/dark/fleet-census.png';
import techSummaryDark from './images/dark/tech-summary.png';
import arriveAgentLaunchPlatformLight from './images/light/arrive-agent-launch-platform.png';
import arriveAgentLaunchPlatformDark from './images/dark/arrive-agent-launch-platform.png';
import finopsLight from './images/light/finops.png';
import finopsDark from './images/dark/finops.png';
import mcpChatLight from './images/light/mcp-chat.png';
import mcpChatDark from './images/dark/mcp-chat.png';
import qualityMaturityLight from './images/light/quality-maturity.png';
import qualityMaturityDark from './images/dark/quality-maturity.png';
import okrCascadeLight from './images/light/okr-cascade.png';
import okrCascadeDark from './images/dark/okr-cascade.png';
import scorecardLeaderboardLight from './images/light/scorecard-leaderboard.png';
import scorecardLeaderboardDark from './images/dark/scorecard-leaderboard.png';

// Add new experimental plugins here before they graduate to the main sidebar
// Place screenshots in:
//   images/light/<plugin-name>.png  — light mode
//   images/dark/<plugin-name>.png   — dark mode
//
// Set `featureFlag` if the card should only appear once the user has enabled
// that flag in Settings → Feature Flags. Useful for plugins whose backing
// service is paid (LLM calls) or otherwise gated — discovery happens via
// internal docs / Slack, not by browsing /preview. Cards without `featureFlag`
// are always visible.
const PREVIEW_PLUGINS: {
  name: string;
  description: string;
  path: string;
  icon: SvgIconComponent;
  tags: string[];
  imageLight: string;
  imageDark: string;
  featureFlag?: string;
}[] = [
  {
    name: 'Fleet Census',
    description:
      'Fleet-wide dashboard showing repository health, vulnerability counts, and a sortable overview of all services across the organisation.',
    path: '/fleet-census',
    icon: StorageIcon,
    tags: ['fleet', 'inventory'],
    imageLight: fleetCensusLight,
    imageDark: fleetCensusDark,
  },
  {
    name: 'Tech Summary',
    description:
      'Interactive breakdown of technology choices across the fleet - languages, runtimes, databases, and deploy targets with drill-down charts.',
    path: '/tech-summary',
    icon: PieChartIcon,
    tags: ['tech-summary'],
    imageLight: techSummaryLight,
    imageDark: techSummaryDark,
  },
  {
    name: 'Modernisation Tracker',
    description:
      'Track the progress of modernisation initiatives across your repositories — scope, coverage, blockers, and ownership per phase.',
    path: '/code-migrations',
    icon: CodeIcon,
    tags: ['modernisation', 'migrations', 'dx', 'platform'],
    imageLight: codeMigrationsLight,
    imageDark: codeMigrationsDark,
  },
  {
    name: 'Agent Launch Platform',
    description:
      'A dashboard to manage Arrive Background Agent trigger rules and monitor triggered agent executions.',
    path: '/arrive-agent-launch-platform',
    icon: PlayArrowIcon,
    tags: ['agents', 'automation', 'jira'],
    imageLight: arriveAgentLaunchPlatformLight,
    imageDark: arriveAgentLaunchPlatformDark,
  },
  {
    name: 'FinOps',
    description:
      'Current AWS spend vs Target (after acting on Datadog cost-optimization recommendations), broken down by VP and drillable into Heads of Engineering.',
    path: '/finops',
    icon: AttachMoneyIcon,
    tags: ['finops', 'cost', 'datadog', 'aws'],
    imageLight: finopsLight,
    imageDark: finopsDark,
  },
  {
    name: 'OKR Cascade',
    description:
      'Cascading OKR tree across all four levels — Arrive → BU → Experience → Team. Browse objectives by quarter, navigate key results, and trace how team goals roll up to company strategy.',
    path: '/okr-cascade',
    icon: TrackChangesIcon,
    tags: ['okr', 'strategy', 'planning'],
    imageLight: okrCascadeLight,
    imageDark: okrCascadeDark,
  },
  {
    name: 'MCP Chat',
    description:
      'Conversational AI assistant grounded in the Arrive catalog via MCP tools. Ask about owners, services, and infrastructure in plain English. Card is hidden until the "mcp-chat" feature flag is enabled in Settings → Feature Flags.',
    path: '/mcp-chat',
    icon: ChatIcon,
    tags: ['ai', 'chat', 'mcp'],
    imageLight: mcpChatLight,
    imageDark: mcpChatDark,
    featureFlag: 'mcp-chat',
  },
  {
    name: 'Quality Maturity',
    description:
      'Radar chart showing the quality maturity score of each product across four quadrants: Alignment, Coverage, Reliability, and Lean. Select a product to drill into its quadrant scores, or view the fleet overview.',
    path: '/quality-maturity',
    icon: TrackChangesIcon,
    tags: ['quality', 'testing', 'maturity', 'radar'],
    imageLight: qualityMaturityLight,
    imageDark: qualityMaturityDark,
  },
  {
    name: 'Scorecard Leaderboard',
    description:
      'Rank every team by its overall and per-scorecard grade in one place.',
    path: '/scorecard-leaderboard',
    icon: EmojiEventsIcon,
    tags: ['scorecard', 'leaderboard', 'teams', 'tech-insights'],
    imageLight: scorecardLeaderboardLight,
    imageDark: scorecardLeaderboardDark,
    featureFlag: 'scorecard-v2',
  },
];

const useStyles = makeStyles(theme => ({
  toolbar: {
    marginBottom: theme.spacing(3),
  },
  banner: {
    marginBottom: theme.spacing(3),
  },
  searchField: {
    minWidth: 300,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  cardMedia: {
    height: 200,
    objectFit: 'cover',
    objectPosition: 'top',
  },
  cardPlaceholder: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
  placeholderIcon: {
    fontSize: '3.5rem',
    color:
      theme.palette.type === 'dark'
        ? theme.palette.grey[600]
        : theme.palette.grey[400],
  },
  cardContent: {
    flexGrow: 1,
    paddingBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  typeIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.3rem',
  },
  name: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  description: {
    marginBottom: theme.spacing(1.5),
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: '3.6em',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  // Status badge - intentionally distinct (amber) to signal experimental state
  previewChip: {
    fontSize: '0.7rem',
    height: 22,
    backgroundColor: theme.palette.type === 'dark' ? '#7c4a00' : '#fff3e0',
    color: theme.palette.type === 'dark' ? '#ffcc80' : '#e65100',
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
  // Tags - default filled chip, matching the AI Marketplace tag style
  tagChip: {
    fontSize: '0.7rem',
    height: 22,
  },
}));

function PreviewPluginCard(
  props: (typeof PREVIEW_PLUGINS)[number] & { onCardClick?: () => void },
) {
  const {
    name,
    description,
    path,
    icon: Icon,
    tags,
    imageLight,
    imageDark,
    onCardClick,
  } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const image = theme.palette.type === 'dark' ? imageDark : imageLight;

  return (
    <Card
      className={classes.card}
      onClick={() => {
        if (onCardClick) onCardClick();
        navigate(path);
      }}
    >
      {image ? (
        <CardMedia
          component="img"
          className={classes.cardMedia}
          image={image}
          alt={`${name} preview`}
        />
      ) : (
        <Box className={classes.cardPlaceholder}>
          <Icon className={classes.placeholderIcon} />
        </Box>
      )}

      <CardContent className={classes.cardContent}>
        <Box className={classes.header}>
          <Icon className={classes.typeIcon} />
          <Typography className={classes.name} variant="subtitle1">
            {name}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.description}
        >
          {description}
        </Typography>

        <Box className={classes.chipRow}>
          <Chip label="PREVIEW" size="small" className={classes.previewChip} />
          {tags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              className={classes.tagChip}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export const PreviewPage = () => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const featureFlags = useApi(featureFlagsApiRef);
  const {
    trackSectionViewed,
    trackPluginClicked,
    trackSearched,
    trackFeedbackLinkClicked,
  } = usePreviewAnalytics();

  // Cards with a `featureFlag` only render when that flag is active.
  // Feature flags are sync-readable but not subscribable, so re-evaluation
  // happens on next mount/refresh after the user toggles in Settings.
  const visiblePlugins = useMemo(
    () =>
      PREVIEW_PLUGINS.filter(
        p => !p.featureFlag || featureFlags.isActive(p.featureFlag),
      ),
    [featureFlags],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return visiblePlugins;
    return visiblePlugins.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q)),
    );
  }, [search, visiblePlugins]);

  // fire preview_section_viewed on mount — count only the cards actually
  // shown to this user (gated cards are excluded).
  useEffect(() => {
    trackSectionViewed(visiblePlugins.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fire preview_searched on search changes (debounced via ref)
  const prevSearchRef = useRef('');
  useEffect(() => {
    if (search === prevSearchRef.current) return;
    if (!search && !prevSearchRef.current) return;
    prevSearchRef.current = search;
    if (search) {
      trackSearched(search, filtered.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filtered.length]);

  return (
    <Page themeId="tool">
      <Header
        title="Preview"
        subtitle="Experimental features and early-stage plugins"
      />
      <Content>
        <Box className={classes.banner}>
          <Alert severity="warning">
            <strong>Preview features</strong> - These plugins are experimental
            and under active development. Functionality, APIs, and data may
            change or be removed without notice.
            <br />
            Share your feedback in{' '}
            <a
              href="https://arrive.enterprise.slack.com/archives/C0A7V3QLS74"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'inherit',
                fontWeight: 700,
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              }}
              onClick={() => trackFeedbackLinkClicked('preview_list')}
            >
              #portfolio-and-api-public
            </a>{' '}
            to help us decide what to promote to the main navigation.
          </Alert>
        </Box>

        <Box className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            label="Search preview plugins"
            variant="outlined"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>

        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
          }}
        >
          {filtered.map((plugin, index) => (
            <PreviewPluginCard
              key={plugin.name}
              {...plugin}
              onCardClick={() =>
                trackPluginClicked(
                  plugin.name,
                  plugin.tags,
                  index + 1,
                  search ? 'search' : 'preview_list',
                )
              }
            />
          ))}
        </Box>
      </Content>
    </Page>
  );
};
